import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';

const WorkflowBuilder = ({ workflows, onSaveWorkflow, onDeleteWorkflow }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [workflowData, setWorkflowData] = useState({
    name: '',
    description: '',
    steps: []
  });

  const riskLevels = [
    { value: 'low', label: 'Low Risk (0-30)' },
    { value: 'medium', label: 'Medium Risk (31-70)' },
    { value: 'high', label: 'High Risk (71-100)' }
  ];

  const approverRoles = [
    { value: 'Marketing Creator', label: 'Marketing Creator' },
    { value: 'Compliance Officer', label: 'Compliance Officer' },
    { value: 'Marketing Director', label: 'Marketing Director' },
    { value: 'Legal Reviewer', label: 'Legal Reviewer' }
  ];

  const handleEditWorkflow = (workflow) => {
    setSelectedWorkflow(workflow);
    setWorkflowData(workflow);
    setIsEditing(true);
  };

  const handleAddStep = () => {
    setWorkflowData(prev => ({
      ...prev,
      steps: [...prev?.steps, {
        id: Date.now(),
        name: '',
        approverRole: '',
        condition: '',
        required: true
      }]
    }));
  };

  const handleUpdateStep = (stepId, field, value) => {
    setWorkflowData(prev => ({
      ...prev,
      steps: prev?.steps?.map(step =>
        step?.id === stepId ? { ...step, [field]: value } : step
      )
    }));
  };

  const handleRemoveStep = (stepId) => {
    setWorkflowData(prev => ({
      ...prev,
      steps: prev?.steps?.filter(step => step?.id !== stepId)
    }));
  };

  const handleSave = () => {
    onSaveWorkflow(workflowData);
    setIsEditing(false);
    setSelectedWorkflow(null);
    setWorkflowData({ name: '', description: '', steps: [] });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSelectedWorkflow(null);
    setWorkflowData({ name: '', description: '', steps: [] });
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Approval Workflows</h2>
            <p className="text-sm text-muted-foreground">Configure content approval processes</p>
          </div>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            New Workflow
          </Button>
        </div>
      </div>
      <div className="p-6">
        {!isEditing ? (
          <div className="space-y-4">
            {workflows?.map((workflow) => (
              <div key={workflow?.id} className="border border-border rounded-lg p-4 hover:shadow-subtle transition-micro">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-medium text-foreground">{workflow?.name}</h3>
                    <p className="text-sm text-muted-foreground">{workflow?.description}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      iconSize={14}
                      onClick={() => handleEditWorkflow(workflow)}
                    >
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      iconSize={14}
                      onClick={() => onDeleteWorkflow(workflow?.id)}
                    >
                    </Button>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="Users" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{workflow?.steps?.length} steps</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">~{workflow?.avgTime || '2-4'} hours</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-green-600" />
                    <span className="text-sm text-foreground">{workflow?.successRate || '94'}% success rate</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Workflow Name"
                type="text"
                placeholder="Enter workflow name"
                value={workflowData?.name}
                onChange={(e) => setWorkflowData(prev => ({ ...prev, name: e?.target?.value }))}
                required
              />
              <Input
                label="Description"
                type="text"
                placeholder="Enter workflow description"
                value={workflowData?.description}
                onChange={(e) => setWorkflowData(prev => ({ ...prev, description: e?.target?.value }))}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-foreground">Approval Steps</h3>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={handleAddStep}
                >
                  Add Step
                </Button>
              </div>

              <div className="space-y-4">
                {workflowData?.steps?.map((step, index) => (
                  <div key={step?.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-medium text-foreground">Step {index + 1}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Trash2"
                        iconSize={14}
                        onClick={() => handleRemoveStep(step?.id)}
                      >
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input
                        label="Step Name"
                        type="text"
                        placeholder="Enter step name"
                        value={step?.name}
                        onChange={(e) => handleUpdateStep(step?.id, 'name', e?.target?.value)}
                      />
                      
                      <Select
                        label="Approver Role"
                        placeholder="Select approver role"
                        options={approverRoles}
                        value={step?.approverRole}
                        onChange={(value) => handleUpdateStep(step?.id, 'approverRole', value)}
                      />
                      
                      <Select
                        label="Trigger Condition"
                        placeholder="Select condition"
                        options={riskLevels}
                        value={step?.condition}
                        onChange={(value) => handleUpdateStep(step?.id, 'condition', value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                iconName="Save"
                iconPosition="left"
                onClick={handleSave}
              >
                Save Workflow
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowBuilder;